import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, CreditCard, Link2, Loader2, Phone, RefreshCw } from "lucide-react";

// PROMPT 91 (2026-09-04): cola de pagos del agente de WhatsApp. El agente deja la reserva
// cargada y PENDIENTE DE PAGO; el link lo arma una persona (Square/Stripe) y lo manda por
// el chat. Acá se marca "link enviado" y después "pagada" (la reserva pasa a confirmada).

const ESTADOS = {
  pendiente_link: { label: "Falta mandar el link", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  link_enviado: { label: "Link enviado, esperando el pago", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  pagada: { label: "Pagada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

function fecha(f, h) {
  if (!f) return "—";
  return h ? `${f} · ${h}` : f;
}

export default function ColaPagosDialog({ open, onOpenChange, onCambio }) {
  const [items, setItems] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accionando, setAccionando] = useState(null);
  const [montos, setMontos] = useState({});

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/pagos");
      setItems(data?.items || []);
      setResumen(data?.resumen || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) cargar();
  }, [open, cargar]);

  const marcarLink = async (r) => {
    setAccionando(r.id);
    try {
      await api.post(`/whatsapp/pagos/${r.id}/link-enviado`);
      toast.success("Anotado: el link ya salió.");
      await cargar();
      onCambio?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAccionando(null);
    }
  };

  const marcarPagada = async (r) => {
    const monto = montos[r.id];
    setAccionando(r.id);
    try {
      await api.post(`/whatsapp/pagos/${r.id}/pagada`, {
        monto: monto === undefined || monto === "" ? null : parseFloat(monto),
      });
      toast.success("Cobro registrado. La reserva quedó confirmada.");
      await cargar();
      onCambio?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAccionando(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cola-pagos-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center justify-between gap-2 pr-6">
            <span className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" /> Cola de pagos
            </span>
            <Button type="button" variant="outline" size="sm" onClick={cargar} data-testid="pagos-refrescar" className="gap-2">
              <RefreshCw className="h-4 w-4" /> Actualizar
            </Button>
          </DialogTitle>
          <DialogDescription>
            Reservas que tomó el agente por WhatsApp y todavía no están cobradas. El link de pago lo armás vos
            (Square o Stripe) y se lo mandás al cliente por el chat; acá queda el registro.
          </DialogDescription>
        </DialogHeader>
        {resumen && (
          <p className="text-xs text-slate-500" data-testid="pagos-resumen">
            {resumen.pendiente_link} sin link · {resumen.link_enviado} esperando el pago
          </p>
        )}
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">No hay reservas esperando cobro.</p>
        ) : (
          <div className="space-y-2">
            {items.map((r) => {
              const e = ESTADOS[r.cobro_estado] || ESTADOS.pendiente_link;
              const pax = (r.adultos || 0) + (r.ninos || 0) + (r.infantes || 0);
              return (
                <Card key={r.id} className="p-3" data-testid={`pago-${r.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">
                          {r.pasajero?.nombre} {r.pasajero?.apellido}
                        </span>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${e.cls}`}>
                          {e.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 mt-0.5">
                        {r.tour_nombre || "—"} · {fecha(r.fecha, r.hora)} · {pax} pax
                      </p>
                      <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className="font-mono">{r.codigo_booking}</span>
                        {r.wa_telefono && (
                          <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {r.wa_telefono}</span>
                        )}
                        {r.agencia && <span>{r.agencia}</span>}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-slate-900">
                        {Number(r.saldo_pendiente || 0).toFixed(2)} {r.moneda || "USD"}
                      </p>
                      <p className="text-[11px] text-slate-400">a cobrar</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2 mt-2 pt-2 border-t border-slate-100">
                    {r.cobro_estado === "pendiente_link" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => marcarLink(r)}
                        disabled={accionando === r.id}
                        data-testid={`pago-link-${r.id}`}
                        className="gap-1.5"
                      >
                        {accionando === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
                        Link enviado
                      </Button>
                    )}
                    <Input
                      value={montos[r.id] ?? ""}
                      onChange={(ev) => setMontos((m) => ({ ...m, [r.id]: ev.target.value }))}
                      placeholder={`${Number(r.saldo_pendiente || 0).toFixed(2)}`}
                      className="h-8 w-28 text-sm"
                      data-testid={`pago-monto-${r.id}`}
                    />
                    <Button
                      size="sm"
                      onClick={() => marcarPagada(r)}
                      disabled={accionando === r.id}
                      data-testid={`pago-pagada-${r.id}`}
                      className="gap-1.5"
                    >
                      {accionando === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                      Marcar pagada
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
